/**
 * The game menu (UX-SPEC §3): sound, theme, help, leave and resign. Desktop
 * shows it as a card in the left column; tablet and mobile open the same
 * content as a bottom sheet from the top bar's overflow button.
 *
 * <GameDialogs/> owns the resign and leave confirmations so they work no
 * matter which variant opened them.
 */
import { useState } from 'react';
import type { GameView, RoomView } from '@wordloom/engine';
import { resignGame } from '../game/actions';
import { opponentSeat, seatName } from '../game/events';
import { useCopyRoomLink } from '../lib/useCopyRoomLink';
import { leave } from '../net/session';
import { useUiStore, type DialogId } from '../state/uiStore';
import { ConfirmDialog } from './ConfirmDialog';
import { IconCheck, IconCopy, IconDoor, IconFlag, IconHelp, IconKeyboard } from './Icons';
import { Modal } from './Modal';
import { SoundToggle } from './SoundToggle';
import { ThemeToggle } from './ThemeToggle';
import { Tooltip } from './Tooltip';
import './GameMenu.css';

interface MenuProps {
  view: RoomView;
  game: GameView;
}

/**
 * Switch from the menu sheet to another dialog. Focus goes to the menu
 * trigger first so the next dialog restores focus there when it closes
 * (the sheet's own buttons are about to unmount).
 */
function switchTo(id: DialogId): void {
  const trigger = document.querySelector<HTMLElement>('[data-menu-trigger]');
  if (trigger && trigger.getClientRects().length > 0) trigger.focus({ preventScroll: true });
  useUiStore.getState().openDialog(id);
}

/** Leave: confirm mid-game (it's a disconnect, not a resignation); direct once it's over. */
function requestLeave(game: GameView): void {
  if (game.status === 'playing') switchTo('leave');
  else leave({ forget: false });
}

function MenuBody({ view, game, inSheet }: MenuProps & { inSheet: boolean }) {
  const playing = game.status === 'playing';
  const { copied, copy } = useCopyRoomLink(view.code);
  return (
    <div className="menu">
      <div className="menu__row">
        <span className="menu__label">Room</span>
        <span className="menu__code">{view.code}</span>
        <Tooltip content="Copy this room’s link">
          <button type="button" className="btn btn--ghost btn--sm menu__copy" onClick={() => void copy()}>
            {copied ? <IconCheck /> : <IconCopy />} {copied ? 'Copied' : 'Copy link'}
          </button>
        </Tooltip>
      </div>
      <div className="menu__row">
        <span className="menu__label">Theme</span>
        <ThemeToggle showLabels={inSheet} />
      </div>
      {inSheet ? (
        <SoundToggle showLabel />
      ) : (
        <div className="menu__row">
          <span className="menu__label">Sound</span>
          <SoundToggle />
        </div>
      )}
      <div className="menu__links">
        <Tooltip content="Rules, scoring and the premium squares">
          <button type="button" className="btn btn--ghost btn--sm menu__link" onClick={() => switchTo('howto')}>
            <IconHelp /> How to play
          </button>
        </Tooltip>
        {/* Keyboard shortcuts mean nothing on a touch-only device: hidden there by CSS. */}
        <Tooltip content="Every keyboard shortcut" shortcut="?">
          <button
            type="button"
            className="btn btn--ghost btn--sm menu__link menu__link--keys"
            onClick={() => switchTo('shortcuts')}
          >
            <IconKeyboard /> Shortcuts
          </button>
        </Tooltip>
        <Tooltip
          content={
            playing
              ? 'Step away — your seat is kept and you can resume from the lobby'
              : 'Back to the lobby — the room stays in your Resume list'
          }
        >
          <button type="button" className="btn btn--ghost btn--sm menu__link" onClick={() => requestLeave(game)}>
            <IconDoor /> Leave game
          </button>
        </Tooltip>
        {playing && (
          <Tooltip content="Concede this game to your opponent">
            <button
              type="button"
              className="btn btn--ghost btn--sm menu__link menu__link--danger"
              onClick={() => switchTo('resign')}
            >
              <IconFlag /> Resign
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/** Desktop: a card in the left column. */
export function GameMenuCard(props: MenuProps) {
  return (
    <section className="card game-menu" aria-labelledby="game-menu-title">
      <h2 id="game-menu-title" className="eyebrow">
        Game
      </h2>
      <MenuBody {...props} inSheet={false} />
    </section>
  );
}

/** Tablet / mobile: the overflow menu as a bottom sheet. */
export function GameMenuSheet(props: MenuProps) {
  const open = useUiStore((s) => s.dialog === 'menu');
  const closeDialog = useUiStore((s) => s.closeDialog);
  return (
    <Modal open={open} onClose={closeDialog} title="Game menu" variant="sheet" className="game-menu-sheet">
      <MenuBody {...props} inSheet />
    </Modal>
  );
}

/** Resign + leave confirmations. */
export function GameDialogs({ view, game }: MenuProps) {
  const dialog = useUiStore((s) => s.dialog);
  const closeDialog = useUiStore((s) => s.closeDialog);
  const [busy, setBusy] = useState(false);
  const oppName = seatName(view, opponentSeat(view));
  const playing = game.status === 'playing';

  const confirmResign = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await resignGame();
    setBusy(false);
    if (ok) closeDialog();
  };

  return (
    <>
      <ConfirmDialog
        open={dialog === 'resign' && (playing || busy)}
        title="Resign this game?"
        body={<p>{oppName} wins this game. You can still ask for a rematch.</p>}
        confirmLabel="Resign"
        tone="danger"
        busy={busy}
        onConfirm={() => void confirmResign()}
        onCancel={closeDialog}
      />
      <ConfirmDialog
        open={dialog === 'leave' && playing}
        title="Leave this game?"
        body={
          <p>
            Your seat is kept — reopen it from the lobby’s Resume list. If you stay away, {oppName} can claim the win.
          </p>
        }
        confirmLabel="Leave"
        onConfirm={() => {
          closeDialog();
          leave({ forget: false });
        }}
        onCancel={closeDialog}
      />
    </>
  );
}
